'use client';

import { Progress, Tooltip } from '@heroui/react';
import { Step } from '../page';
import { FaCheck } from 'react-icons/fa6';
import { useStepNavigation } from '../StepNavigationContext';
import { useStepAccess } from '../utils/useStepAccess';
import { useScheduler } from '../SchedulerContext';

const Stepper = ({ steps }: { steps: Step[] }) => {
  const { setActiveStep } = useStepNavigation();
  const { companyStyles, bookingStatus, isSendingQuote } = useScheduler();
  const isBookingActive = bookingStatus !== 'idle';

  // Access policy is shared with the summary bar and the navigation guard so the
  // three can never disagree about which steps are open.
  const {
    activeStep,
    canAccessStep,
    getStepValidation,
    getBlockingFields,
    isNavigationLocked,
  } = useStepAccess();

  // A step is clickable when the data allows it AND no request is in flight.
  const isStepClickable = (stepOrder: number) =>
    !isNavigationLocked && canAccessStep(stepOrder);

  const handleStepClick = (stepOrder: number) => {
    if (isStepClickable(stepOrder)) {
      setActiveStep(stepOrder);
    }
  };

  return (
    <div className='flex gap-2' style={companyStyles.cssVars}>
      {steps.map((step, index) => (
        <StepButton
          key={step.name}
          step={step}
          active={activeStep >= step.order}
          index={index}
          setActiveStep={handleStepClick}
          activeStep={activeStep}
          canAccess={isStepClickable(step.order)}
          isComplete={getStepValidation(step.order).isValid}
          blockingFields={getBlockingFields(step.order)}
          companyStyles={companyStyles}
          isBookingActive={isBookingActive}
          isSendingQuote={isSendingQuote}
        />
      ))}
    </div>
  );
};

const StepButton = ({
  step,
  active,
  index,
  activeStep,
  setActiveStep,
  canAccess,
  isComplete,
  blockingFields,
  companyStyles,
  isBookingActive,
  isSendingQuote,
}: {
  step: Step;
  active: boolean;
  index: number;
  activeStep: number;
  setActiveStep: (step: number) => void;
  canAccess: boolean;
  isComplete: boolean;
  /** What still has to be filled in before this step opens. */
  blockingFields: string[];
  companyStyles: {
    primaryButtonStyles: { backgroundColor?: string; color?: string };
    secondaryButtonStyles: { backgroundColor?: string; color?: string };
    primaryButtonClassName: string;
    secondaryButtonClassName: string;
    primaryButtonClassNameWithVars: string;
    secondaryButtonClassNameWithVars: string;
    cssVars: React.CSSProperties;
    companyColor: string | undefined;
    colorSecondary: string | undefined;
  };
  isBookingActive: boolean;
  isSendingQuote: boolean;
}) => {
  const tooltipContent = (() => {
    if (isBookingActive || isSendingQuote) {
      return 'Please wait while your request is being processed...';
    }
    if (!canAccess) {
      // Name what is actually missing rather than the generic "complete previous
      // steps" — otherwise a customer blocked on an unanswered required question
      // has no way to tell which one.
      if (blockingFields.length === 0) {
        return `Complete previous steps to access ${step.name}`;
      }
      return (
        <div className='max-w-xs'>
          <p className='mb-1 font-semibold'>Complete these to continue:</p>
          <ul className='list-disc pl-4 text-xs'>
            {blockingFields.map((field) => (
              <li key={field}>{field}</li>
            ))}
          </ul>
        </div>
      );
    }
    if (isComplete && activeStep < step.order) {
      return `${step.name} - Complete`;
    }
    return step.name;
  })();

  return (
    <div className='flex items-center gap-2'>
      {index !== 0 && (
        <Progress
          className='w-12'
          size='sm'
          aria-label={step.name}
          value={activeStep >= step.order ? 100 : 0}
          classNames={{
            indicator:
              activeStep >= step.order
                ? companyStyles.primaryButtonClassNameWithVars
                : '',
          }}
        />
      )}
      <Tooltip
        content={tooltipContent}
        placement='bottom'
        delay={400}
        closeDelay={0}
        color='foreground'
        showArrow
      >
        <button
          className={`flex flex-col items-center justify-center gap-1 outline-none transition-all ${
            !canAccess || isBookingActive || isSendingQuote
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-pointer hover:opacity-80'
          }`}
          onClick={() =>
            canAccess &&
            !isBookingActive &&
            !isSendingQuote &&
            setActiveStep(step.order)
          }
          disabled={!canAccess || isBookingActive || isSendingQuote}
        >
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-full  p-2 text-lg  ${
              activeStep >= step.order
                ? 'text-white'
                : canAccess
                  ? 'bg-slate-200 text-themeBlack'
                  : 'bg-slate-100 text-slate-400'
            }`}
            style={
              activeStep >= step.order
                ? { backgroundColor: companyStyles.companyColor || '#006FEE' }
                : undefined
            }
          >
            {step.icon}
            {/* {activeStep > step.order ? <FaCheck /> : step.icon} */}
          </div>
          {/* <span>{step.name}</span> */}
        </button>
      </Tooltip>
    </div>
  );
};

export default Stepper;
